import React, { useMemo, useCallback } from 'react';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import { MenuItem } from '@mui/material';
import { number, string, func, bool, elementType } from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';
import InputField from '#component/form/input';
import Autocomplete from '#component/form/autocomplete';
import { createTitle as createAlertTitle } from '#component/project/form/alerts/alert-item/helper';
import DevicePopup from '#component/device/devices-popup/device-popup';
import { AddDevicesButton, DeleteDevicesButton } from '#component/device/devices-popup/components/action-buttons/shipment';
import {
  useDevicePopupChangesShipment,
  useFloatDevicesSyncShipment
} from '#component/device/devices-popup/hooks';
import {
  setFilter,
  setDeviceType,
  changePage,
  changeOrderAndOrderBy,
  fetchCargoDevices,
  setFormData
} from '#action/shipment/cargo-devices-popup';
import { getShipmentStatus } from '#component/shipment/helper';
import DeviceField from '#component/general/form-device-field';
import { getAllCargosDevices } from '#component/shipment/form/cargo/helper';

const SelectionFields = props => {
  const {
    member,
    setValue: propsSetValue,
    index,
    handleContainerChange,
    handleProductChange,
    disabled,
    descriptionField,
    initialProduct,
    initialPackage,
    shipmentId,
    cargoItem,
    isCopiedShipment
  } = props;
  const methods = useFormContext();
  const { control, watch, setValue: contextSetValue } = methods || {};
  const setValue = propsSetValue || contextSetValue;

  const watchCargos = watch?.('cargo');
  const cargos = watchCargos;
  const watchedDevices = useWatch({ control, name: `${member}.devices` });
  const selectedDevices = watchedDevices || cargos?.[index]?.devices;
  const cargosDevices = getAllCargosDevices(cargos);
  const measurementSystem = useSelector(s => s.account?.measurementSystem, shallowEqual);
  // initialFormDevices: the devices that were in the cargo item when the form loaded
  const initialFormDevices = useMemo(
    () => cargoItem?.devices ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // only read once on mount — this is the "initial" snapshot
  );

  const projectId = useSelector(s => {
    const sh = s.shipment?.id ? s.shipment : { ...s.shipment, ...(s.shipmentForm?.shipment || {}) };
    return sh?.projectId || (typeof sh?.project === 'object' ? sh?.project?.id : sh?.project);
  }, shallowEqual);
  const projectsAlerts = useSelector(s => s.projectsAlerts, shallowEqual);
  const projectAlerts = useMemo(() => projectsAlerts[projectId] ?? [], [projectsAlerts, projectId]);
  const products = useSelector(s => s.projectsProducts?.[projectId] ?? [], shallowEqual);
  const packages = useSelector(s => s.projectsPackages?.[projectId] ?? [], shallowEqual);
  const shipment = useSelector(s => s.shipment);

  const { project } = shipment;

  const setData = useCallback(d => {
    const formattedDevices = (d ?? []).map(device => (typeof device === 'object' && device !== null ? {
      ...device,
      label: device.label || device.title || device.uid || `${device.id}`,
      value: device.value || device.id
    } : device));
    setValue?.(`${member}.devices`, formattedDevices, { shouldDirty: true, shouldValidate: true });
  }, [setValue, member]);

  const alertId = cargos?.[index]?.projectAlertId;
  const shipmentNotStartedStatus = getShipmentStatus(shipment)?.code === 1;

  const isAlertFieldDisabled = !isCopiedShipment &&
    (disabled || (!shipmentNotStartedStatus && !!alertId));

  const handleAlertSettingsBlur = e => {
    setValue?.(`${member}.projectAlertId`, e.target.value || null);
  };

  const onAlertSettingsChange = (event, newValue) => {
    setData(selectedDevices?.map(d => ({ ...d, alertOn: !!newValue, showAlertIcon: !!newValue })));
  };

  const memoizedCargoItem = useMemo(() => {
    const { devices, ...rest } = cargoItem || {};
    return rest;
  }, [cargoItem]);

  const shipmentData = useMemo(
    () => ({
      shipmentId,
      alertId,
      formName: 'ShipmentCargoForm',
      formDevicesField: 'cargo',
      groupData: memoizedCargoItem ?? [],
      formIndex: index,
      member,
      projectId,
      // Pass the form devices directly so the popup hooks read them from here
      formDevices: selectedDevices ?? [],
      initialFormDevices,
      allCargos: cargos ?? []
    }),
    [
      memoizedCargoItem, shipmentId, alertId, index, member,
      projectId, selectedDevices, initialFormDevices, cargos
    ]
  );

  const devicePopupActions = useMemo(() => ({
    setFilter,
    setDeviceType,
    changePage,
    changeOrderAndOrderBy,
    setFormData,
    fetchDevices: fetchCargoDevices
  }), []);

  const devicePopupHooks = useMemo(() => ({
    usePopupChanges: useDevicePopupChangesShipment,
    useDevicesSync: useFloatDevicesSyncShipment
  }), []);

  const memoProject = useMemo(() => ({ id: projectId }), [projectId]);

  const devicePopupData = useMemo(() => ({
    project: shipmentId ? project : memoProject,
    openedFromCreateForm: true,
    member,
    formIndex: index,
    data: shipmentData,
    customComponents: { AddDevicesButton, DeleteDevicesButton },
    customActions: devicePopupActions,
    customHooks: devicePopupHooks,
    alertId,
    setFormDevices: setData
  }), [
    memoProject,
    alertId,
    devicePopupActions,
    devicePopupHooks,
    index,
    member,
    project,
    setData,
    shipmentData,
    shipmentId
  ]);

  const alertOptions = useMemo(() => projectAlerts
    .filter(alert => !alert.archived || alert.id === alertId)
    .map(alert => (
      <MenuItem key={alert.id} value={alert.id}>
        {createAlertTitle(alert, 'metric', measurementSystem)}
      </MenuItem>
    )), [projectAlerts, measurementSystem, alertId]);

  const StyledFieldComponent = props.StyledField || 'div';

  return (
    <>
      <StyledFieldComponent>
        <Controller
          name={`${member}.goods`}
          control={control}
          render={({ field, fieldState }) => (
            <Autocomplete
              {...field}
              meta={{ ...fieldState, isSubmitted: methods?.formState?.isSubmitted }}
              fullWidth
              options={products.map(prod => prod.label)}
              onChange={(e, value) => {
                field.onChange(value);
                const selectedProd = products.find(p => p.label === value);
                handleProductChange(selectedProd);
              }}
              disabled={disabled || !!initialProduct}
              inputProps={{ label: 'Product', variant: 'outlined' }}
            />
          )}
        />
      </StyledFieldComponent>
      {descriptionField && <StyledFieldComponent>{descriptionField}</StyledFieldComponent>}
      <StyledFieldComponent>
        <Controller
          name={`${member}.container`}
          control={control}
          render={({ field, fieldState }) => (
            <Autocomplete
              {...field}
              meta={{ ...fieldState, isSubmitted: methods?.formState?.isSubmitted }}
              fullWidth
              options={[...packages.map(pack => pack.label), 'Custom']}
              onChange={(e, value) => {
                field.onChange(value);
                const selectedPack = packages.find(p => p.label === value);
                handleContainerChange(selectedPack || { label: value });
              }}
              disabled={disabled || !!initialPackage}
              inputProps={{ label: 'Package type', variant: 'outlined' }}
            />
          )}
        />
      </StyledFieldComponent>
      <StyledFieldComponent>
        <Controller
          name={`${member}.projectAlertId`}
          control={control}
          render={({ field, fieldState }) => (
            <InputField
              {...field}
              meta={{ ...fieldState, isSubmitted: methods?.formState?.isSubmitted }}
              label="Alert settings"
              variant="outlined"
              fullWidth
              onChange={e => {
                field.onChange(e.target.value);
                onAlertSettingsChange(e, e.target.value);
              }}
              onBlur={e => {
                field.onBlur();
                handleAlertSettingsBlur(e);
              }}
              select
              disabled={isAlertFieldDisabled}
            >
              <MenuItem value={null}>
                None
              </MenuItem>
              {alertOptions}
            </InputField>
          )}
        />
      </StyledFieldComponent>
      <StyledFieldComponent>
        <Controller
          name={`${member}.devices`}
          control={control}
          render={({ field, fieldState }) => {
            const { isSubmitted } = methods.formState;
            return (
              <DeviceField
                {...field}
                meta={{ ...fieldState, isSubmitted }}
                id={`device-cargo-${index}filter-multiple-select`}
                label="Devices"
                placeholder="Devices"
                disabled={disabled}
                project={project}
                openedFromCreateForm={true}
                formData={selectedDevices}
                allFormDevices={cargosDevices}
                handleDeviceSetData={setData}
                devicePopupData={devicePopupData}
                DevicePopupComponent={DevicePopup}
                alertId={alertId}
              />
            );
          }}
        />
      </StyledFieldComponent>
    </>
  );
};

export default SelectionFields;

SelectionFields.propTypes = {
  member: string.isRequired,
  index: number.isRequired,
  StyledField: elementType,
  handleContainerChange: func.isRequired,
  handleProductChange: func.isRequired,
  disabled: bool
};
